import { createContext, useEffect, useState, type ReactNode } from "react";
import type { UsuarioResponse } from "@/types/usuario-response";
import inovaone_api from "@/services/inovaone_api";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { alert } from "@/contexts/AlertModalContext";
import { MfaRequiredError } from "@/lib/mfaError";
import { enviarTelemetriaLogin } from "@/services/telemetry";

export type UsuarioContextType = {
  usuario: UsuarioResponse | null;
  login: (
    usuario: string,
    senha: string,
    tipo?: string,
    mfaToken?: string,
  ) => Promise<UsuarioResponse>;
  logout: () => void;
  entrarComHandoff: (handoff: string) => Promise<UsuarioResponse>;
};

export const UsuarioContext = createContext<UsuarioContextType | undefined>(
  undefined,
);

export function UsuarioProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioResponse | null>(null);
  const { setIsLoading } = useGlobalLoader();

  /**
   * Entra com uma passagem de sessão emitida por outro sistema da plataforma.
   *
   * Substitui o `loginAutomatico` removido daqui, que carregava um bearer fixo
   * no bundle público, consultava /usuario de qualquer conta com ele e
   * reautenticava com a senha que vinha na resposta.
   *
   * Aqui nada disso trafega: o token é aleatório, vale segundos, serve uma vez
   * só e o servidor devolve a sessão pronta — mesmo formato do /login.
   */
  const entrarComHandoff = async (handoff: string): Promise<UsuarioResponse> => {
    setIsLoading(true);
    try {
      const resposta = await inovaone_api.post<UsuarioResponse>("/handoff/consumir", {
        token: handoff,
      });

      const { access_token, id_conta } = resposta.data;
      localStorage.setItem("access_token", access_token);

      // Sessão aberta pelo suporte (Painel de Dados do InovaSupport) e não pelo
      // próprio dono da conta: fica marcada para o layout mostrar a faixa de
      // aviso. Vai no sessionStorage — morre com a aba, igual à sessão.
      const dados = resposta.data as UsuarioResponse & {
        handoff_origem?: string;
        handoff_suporte?: { nome?: string; email?: string };
      };
      if (dados.handoff_origem === "suporte") {
        sessionStorage.setItem(
          "suporte_sessao",
          JSON.stringify({
            nome: dados.handoff_suporte?.nome || "",
            email: dados.handoff_suporte?.email || "",
            cliente: dados.nome || dados.login || "",
            inicio: new Date().toISOString(),
          }),
        );
      } else {
        sessionStorage.removeItem("suporte_sessao");
      }

      const usuarioLocalInfo: UsuarioResponse = {
        _id: resposta.data._id,
        nome: resposta.data.nome,
        access_token,
        id_conta,
        login: resposta.data.login,
        mfa_enabled: resposta.data.mfa_enabled === true,
      };

      localStorage.setItem("usuario_local_info", JSON.stringify(usuarioLocalInfo));
      inovaone_api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      inovaone_api.defaults.headers.common["id_conta"] = id_conta;
      setUsuario(usuarioLocalInfo);

      return usuarioLocalInfo;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (
    login: string,
    senha: string,
    tipo: string = "BO",
    mfaToken?: string,
  ): Promise<UsuarioResponse> => {
    setIsLoading(true);
    try {
      const response_login = await inovaone_api.post<UsuarioResponse>(
        "/login",
        {
          login,
          senha,
          tipo,
          ...(mfaToken ? { mfaToken } : {}),
        },
      );
      const { access_token, id_conta } = response_login.data;
      localStorage.setItem("access_token", access_token);

      let usuarioLocalInfo: UsuarioResponse = {
        _id: response_login.data._id,
        nome: response_login.data.nome,
        access_token: access_token,
        id_conta: id_conta,
        login: response_login.data.login,
        mfa_enabled: response_login.data.mfa_enabled === true,
      };

      localStorage.setItem(
        "usuario_local_info",
        JSON.stringify(usuarioLocalInfo),
      );
      inovaone_api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      inovaone_api.defaults.headers.common["id_conta"] = id_conta;
      setUsuario(usuarioLocalInfo);
      // Telemetria para o Painel de Dados (não bloqueia o login).
      void enviarTelemetriaLogin(usuarioLocalInfo);
      return usuarioLocalInfo;
    } catch (error: any) {
      // A API pede o segundo fator (TOTP).
      if (error?.response?.data?.mfaRequired) {
        throw new MfaRequiredError(!!error.response.data.error);
      }
      throw new Error("Erro ao realizar login, verifique suas credenciais.");
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("usuario_local_info");
      localStorage.removeItem("bannerFechado");
      localStorage.removeItem("permitirItensRepetidos");
      inovaone_api.defaults.headers.common.Authorization = "";
      inovaone_api.defaults.headers.common["id_conta"] = "";
      window.location.href = "/auth";
    } catch (error) {
      alert.error("Erro ao realizar logout.");
      throw new Error("Erro ao realizar logout.");
    }
  };

  useEffect(() => {
    const access_token = localStorage.getItem("access_token");
    if (access_token) {
      inovaone_api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    }
    const usuarioLocalInfo = localStorage.getItem("usuario_local_info");

    if (usuarioLocalInfo) {
      const parsedUsuario = JSON.parse(usuarioLocalInfo) as UsuarioResponse;
      setUsuario(parsedUsuario);
      inovaone_api.defaults.headers.common["id_conta"] = parsedUsuario.id_conta;
    }
  }, []);

  return (
    <UsuarioContext.Provider
      value={{ usuario, login, logout, entrarComHandoff }}
    >
      {children}
    </UsuarioContext.Provider>
  );
}
